"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="120751" className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-[3px] flex items-center justify-center mb-6">
          <CheckCircle2 className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif text-foreground mb-2">Tack för din ansökan</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-[3px] px-8">
          Skicka en ny ansökan
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Namn är obligatoriskt",
        minLength: {
          value: 2,
          message: "Namnet måste vara minst 2 tecken"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Fullständigt namn</FormLabel>
              <FormControl>
                <Input placeholder="Erik Andersson" className="bg-background border-input focus-visible:ring-primary focus-visible:border-primary rounded-[3px]" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">Ange namnet på kontaktpersonen.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-postadress är obligatorisk",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ogiltig e-postadress"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">E‑postadress</FormLabel>
              <FormControl>
                <Input type="email" placeholder="erik@foretag.se" className="bg-background border-input focus-visible:ring-primary focus-visible:border-primary rounded-[3px]" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">Vi använder denna för all korrespondens.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Ort / Stad</FormLabel>
              <FormControl>
                <Input type="text" placeholder="Stockholm" className="bg-background border-input focus-visible:ring-primary focus-visible:border-primary rounded-[3px]" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">Staden där din verksamhet bedrivs.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "Typ av verksamhet är obligatoriskt"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Typ av verksamhet</FormLabel>
              <FormControl>
                <Input placeholder="t.ex. Åkeri eller Flottoperatör" className="bg-background border-input focus-visible:ring-primary focus-visible:border-primary rounded-[3px]" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">Beskriv kort din nuvarande roll.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Information om erfarenhet är obligatoriskt",
      minLength: {
        value: 10,
        message: "Beskrivningen måste vara minst 10 tecken"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Erfarenhet och resurser</FormLabel>
            <FormControl>
              <Textarea placeholder="Berätta kort om din erfarenhet av fordonsflottor..." className="min-h-[120px] resize-y bg-background border-input focus-visible:ring-primary focus-visible:border-primary rounded-[3px]" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">Inkludera gärna antal fordon i din nuvarande flotta.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-[3px] text-destructive text-sm">
          {submitError}
        </div>}

      <div className="pt-4 flex flex-col items-end gap-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto rounded-[3px] px-10 h-12 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-none">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-5" />
              Bearbetar...
            </> : <>
              Skicka intresseanmälan
              <Send className="ml-2 size-4" />
            </>}
        </Button>
        <p className="text-[10px] text-muted-foreground max-w-xs text-right">
          För frågor gällande personuppgifter och integritet, kontakta oss på robotaxi@miragomedia.com.
        </p>
      </div>
    </Form>;
}
export default function KontaktaOss() {
  return <section id="kontaktformulär" className="relative bg-secondary py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full max-w-3xl opacity-10 pointer-events-none -translate-x-1/4 -translate-y-1/4 md:-translate-y-1/6 mix-blend-multiply dark:mix-blend-screen">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592905/img/ethereal_sage_branch_illustration.png" alt="" className="w-full h-auto object-contain" aria-hidden="true" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <h2 className="text-4xl md:text-5xl font-serif text-foreground mb-6 tracking-tight">
            Bli en del av operatörsnätverket
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Vi skalar upp vår flotta av autonoma robotaxis. Fyll i formuläret nedan för att påbörja din resa som certifierad operatör i vårt ekosystem.
          </p>
        </div>

        <div className="max-w-[600px] mx-auto">
          <Card className="bg-card border-none shadow-lg shadow-foreground/5 overflow-hidden rounded-[3px]">
            <CardHeader className="pb-0 pt-8 px-8 md:px-12 text-center">
              <CardTitle className="sr-only">Intresseanmälan för operatörer</CardTitle>
              <CardDescription className="sr-only">Fyll i dina uppgifter för att ansöka om att bli operatör.</CardDescription>
            </CardHeader>
            <CardContent className="p-8 md:p-12">
              <FormProvider formKey="contact_form_privacypolicy" formId="contact_form_privacypolicy" sectionName="kontaktformulär" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              subject: {
                required: true
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Skicka intresseanmälan" successMessage="Tack för din ansökan. Vårt team granskar din profil och återkommer till dig inom 48 timmar." errorMessage="Ett fel uppstod när ansökan skulle skickas. Vänligen försök igen senare.">
                <ContactFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}